import { useCallback, useState } from "react";
import appLogger from "../../../infra/logger/appLogger";
import { useAppDispatch, useAppSelector } from "../../hook";
import { useTranslation } from "../../localization";
import { RateLimitFlows, useRateLimitErrors } from "../../miscellaneous";
import { suggestCharacterField, suggestCharacterTags } from "../characterSlice";
const suggestionLogger = appLogger.withTag("Suggestion");
export default function useSuggestion() {
    const dispatch = useAppDispatch();
    const { getMessage: getRateLimitErrorMessage } = useRateLimitErrors();
    const { t } = useTranslation();
    const { loading, loadingType } = useAppSelector((state) => state.character);
    const [suggestionError, setSuggestionError] = useState(null);
    const getSuggestedTags = useCallback(async (characterFields) => {
        try {
            setSuggestionError(null);
            const result = await dispatch(suggestCharacterTags(characterFields)).unwrap();
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = getRateLimitErrorMessage({
                errorCode: apiError?.errorCode,
                skipDefaultMessage: true,
                errorContext: {
                    flow: RateLimitFlows.SUGGEST_TAGS,
                },
            }) ||
                apiError.message ||
                t("error:fallbackError");
            setSuggestionError({
                errorType: RateLimitFlows.SUGGEST_TAGS,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            suggestionLogger.error(`Failed to fetch tag suggestions: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, getRateLimitErrorMessage, t]);
    const onUserSuggestField = useCallback(async (body) => {
        try {
            setSuggestionError(null);
            const result = await dispatch(suggestCharacterField(body)).unwrap();
            return { data: result, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorMessage = getRateLimitErrorMessage({
                errorCode: apiError?.errorCode,
                skipDefaultMessage: true,
                errorContext: {
                    flow: RateLimitFlows.SUGGEST_FIELD,
                },
            }) ||
                apiError.message ||
                t("error:fallbackError");
            setSuggestionError({
                errorType: RateLimitFlows.SUGGEST_FIELD,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            suggestionLogger.error(`Failed to fetch fields suggestions: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [dispatch, getRateLimitErrorMessage, t]);
    return {
        loading,
        loadingType,
        getSuggestedTags,
        onUserSuggestField,
        errorType: suggestionError?.errorType || null,
        errorCode: suggestionError?.errorCode || null,
        errorMessage: suggestionError?.errorMessage || null,
    };
}
